/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/**
* Create a readable stream for generating pseudorandom numbers drawn from a triangular distribution.
*
* @module @stdlib/random-streams-triangular
*
* @example
* import inspectStream from '@stdlib/streams-node-inspect-sink';
* import randomStream from '@stdlib/random-streams-triangular';
*
* function log( chunk ) {
*    console.log( chunk.toString() );
* }
*
* var opts = {
*     'iter': 10
* };
*
* var stream = randomStream( 2.0, 5.0, 4.0, opts );
*
* stream.pipe( inspectStream( log ) );
*
* @example
* import randomStream from '@stdlib/random-streams-triangular';
*
* var opts = {
*     'sep': ',',
*     'objectMode': false,
*     'encoding': 'utf8',
*     'highWaterMark': 64
* };
*
* var createStream = randomStream.factory( opts );
*
* // Create 10 identically configured streams...
* var streams = [];
* var i;
* for ( i = 0; i < 10; i++ ) {
*     streams.push( createStream( 2.0, 5.0, 4.0 ) );
* }
*
* @example
* import inspectStream from '@stdlib/streams-node-inspect-sink';
* import randomStream from '@stdlib/random-streams-triangular';
*
* function log( v ) {
*    console.log( v );
* }
*
* var opts = {
*     'iter': 10
* };
*
* var stream = randomStream.objectMode( 2.0, 5.0, 4.0, opts );
*
* stream.pipe( inspectStream.objectMode( log ) );
*/


// MODULES //

import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import main from './main.js';
import objectMode from './object_mode.js';
import factory from './factory.js';


// MAIN //

setReadOnly( main, 'objectMode', objectMode );
setReadOnly( main, 'factory', factory );


// EXPORTS //

export default main;
